import React, { SFC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Grid,
    CircularProgress,
    Card,
    CardContent,
    Typography,
    ListItem,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import { PricingConditionModel } from '../../../shared/types/pricingCondition'
import SearchIcon from '@material-ui/icons/Search'
import moment from 'moment'
import { getPricingConditionValidity } from '../pricingConditionValidity'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    progress: {
        textAlign: 'center',
        position: 'relative',
        top: '5px'
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    input: {
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px',
            fontSize: '0.9rem',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface PricingConditionListProps {
    pricingConditions: PricingConditionModel[]
    selectedPricingCondition?: PricingConditionModel
    search: string
    onSearchChange(value: string): void
    onPricingConditionSelected(pricingCondition: PricingConditionModel): void
}

const PricingConditionList: SFC<PricingConditionListProps> = ({
    pricingConditions,
    selectedPricingCondition,
    search,
    onSearchChange,
    onPricingConditionSelected,
}) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const pricingConditionsExecuted = useSelector((state: any) => state.pricingConditions.items.executed)

    // Pridanie závislosti, aby hook reagoval na príchod a zmeny dát
    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper');
    
    // Fix pre okamžité prerenderovanie výšky po naplnení zoznamu
    useEffect(() => {
        if (pricingConditions && pricingConditions.length > 0) {
            window.dispatchEvent(new Event('resize'));
        }
    }, [pricingConditions]);

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value)
    }

    return (
        <>
            <Grid container>
                <Grid item xs={10} className={classes.search}>
                    <TextField
                        fullWidth
                        variant="filled"
                        placeholder={t('pricingConditions.search')}
                        value={search}
                        onChange={handleSearchChanged}
                        className={classes.input}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={2} className={classes.progress}>
                    {!pricingConditionsExecuted && <CircularProgress size={30} />}
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {pricingConditionsExecuted && pricingConditions.length === 0 ? (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('pricingConditions.noPricingConditions')} />
                        </div>
                    ) : (
                        pricingConditions.map((pricingCondition) => (
                            <Card key={pricingCondition.id} className={classes.listItem} elevation={1}>
                                <ListItem
                                    button
                                    // Bezpečné porovnávanie ID miesto referencie objektu
                                    selected={pricingCondition.id === selectedPricingCondition?.id}
                                    onClick={() => onPricingConditionSelected(pricingCondition)}
                                    className={classes.listItemButton}
                                >
                                    <div className={classes.listContentWrapper}>
                                        <CardContent className={classes.listContent}>
                                            {/* exspirovany cennik sivo, buduci s datumom zaciatku platnosti */}
                                            <Typography
                                                variant="h6"
                                                style={
                                                    getPricingConditionValidity(pricingCondition) === 'expired'
                                                        ? { color: '#9e9e9e' }
                                                        : undefined
                                                }
                                            >
                                                {pricingCondition.name}
                                            </Typography>
                                            {getPricingConditionValidity(pricingCondition) === 'expired' && (
                                                <Typography variant="caption" style={{ color: '#9e9e9e' }}>
                                                    {t('pricingConditions.expiredAt', {
                                                        date: moment(pricingCondition.validTo).format('DD.MM.YYYY'),
                                                    })}
                                                </Typography>
                                            )}
                                            {getPricingConditionValidity(pricingCondition) === 'upcoming' && (
                                                <Typography variant="caption" color="primary">
                                                    {t('pricingConditions.validFromDate', {
                                                        date: moment(pricingCondition.validFrom).format('DD.MM.YYYY'),
                                                    })}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </div>
                                </ListItem>
                            </Card>
                        ))
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default PricingConditionList